import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, PlayCircleIcon, UsersIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Reveal } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { HeroArcs } from '../components/sections/HeroVisual';
import { Photo } from '../components/ui/Photo';
import { PHOTOS } from '../data/photos';
import { Seo } from '../components/Seo';
import { SessionTime, sessionDateLabel } from '../components/webinars/SessionTime';
import { WEBINARS, SESSION_TIME, nextWebinar, pastWebinars, webinarStatus } from '../data/webinars';
import { breadcrumbSchema, canonical, SITE } from '../lib/seo';
export function Webinars() {
  const featured = nextWebinar();
  const replays = pastWebinars();
  const schema = [{
    '@type': 'EventSeries',
    '@id': `${canonical('/webinars')}#series`,
    name: 'Cavalla 2027 Webinar Programme',
    description: 'A monthly webinar series for government officials on national plan delivery, government data, responsible AI in the public service, and development finance. Every session is the third Thursday of the month at 14:00 GMT and runs for sixty minutes.',
    url: canonical('/webinars'),
    organizer: {
      '@id': `${SITE.url}/#organisation`
    },
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    inLanguage: 'en-GB'
  }, breadcrumbSchema([{
    name: 'Home',
    path: '/'
  }, {
    name: 'Webinars',
    path: '/webinars'
  }])];
  return <>
      <Seo title="Webinars for government: the 2027 programme" description="A monthly webinar for government officials on plan delivery, government data and responsible AI. Third Thursday of the month, 14:00 GMT, sixty minutes, free to attend." path="/webinars" jsonLd={schema} data-id="element-3531" data-mp="2ywi0f56n5o2d" />

      {/* Hero */}
      <section className="surface-dark relative isolate overflow-hidden" data-id="element-3532" data-mp="2fz71jlox8wng">
        <HeroArcs className="pointer-events-none absolute -right-12 top-0 h-[360px] w-[360px] opacity-80" data-id="element-3533" data-mp="26c4fnueeu1qe" />
        <Container className="relative grid gap-12 py-14 sm:py-20 lg:grid-cols-[1.2fr_0.8fr] lg:items-center" data-id="element-3534" data-mp="2bb7ir9atxxvq">
          <div data-id="element-3535" data-mp="1wegjd015om64">
          <Kicker data-id="element-3536" data-mp="1weq8kledmpar">The 2027 programme</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[34px] font-light leading-tight text-white sm:text-[50px]" data-id="element-3537" data-mp="1wg1gmxeuuj7o">
            One hour a month with the people delivering national plans.
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-3538" data-mp="2p4ykvwm8efhf">
            Twelve sessions for officials in ministries, agencies and revenue authorities, on the problems
            that decide whether a plan is delivered: seeing progress, reporting it credibly, turning
            government data into decisions, and putting AI to work safely.
          </p>
          <p className="mt-7 text-[15px] text-white/72" data-id="element-3539" data-mp="37zdltrs3si7w">
            Every session is the third Thursday of the month at {SESSION_TIME.full}. Sixty minutes, free to
            attend, and recorded.
          </p>
          {featured && <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-3540" data-mp="wemv9lz45e6v">
              <Button to={`/webinars/${featured.slug}`} size="lg" shape="pill" data-id="element-3541" data-mp="wcacj6lj6zsr">
                Register for the next session
              </Button>
              <Button href="#calendar" variant="reversed" size="lg" shape="pill" data-id="element-3542" data-mp="164zcp5bmsr8s">
                See the full calendar
              </Button>
            </div>}
          </div>
          <Photo photo={PHOTOS.workshopRoom} aspect="aspect-[4/3]" tone="dark" eager data-id="element-3543" data-mp="dg27a2siqoxg" />
        </Container>
      </section>

      {/* Next session */}
      {featured && <Section aria-labelledby="next-session" data-id="element-3544" data-mp="ndgd78gtfvbi">
          <SectionHeader id="next-session" kicker="Next session" title={featured.title} data-id="element-3545" data-mp="ncn8ed1f3fw0" />
          <div className="mt-8 grid gap-10 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-3546" data-mp="22svvmgujjyn6">
            <div data-id="element-3547" data-mp="1yt4y7uaiek9o">
              <p className="max-w-prose text-[17px] leading-relaxed text-body" data-id="element-3548" data-mp="1yrk4u7zxl7de">{featured.description}</p>
              <dl className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-3549" data-mp="2u43e9gtasq5j">
                <div data-id="element-3550" data-mp="2y43pytp0in8w">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3551" data-mp="2y5evoz20q52n">
                    Who it is for
                  </dt>
                  <dd className="mt-2 text-[16px] text-ink" data-id="element-3552" data-mp="335t2kdrvv8z3">{featured.audience}</dd>
                </div>
                <div data-id="element-3553" data-mp="3d1md5rm1kse8">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3554" data-mp="3d25up77s5erq">Focus</dt>
                  <dd className="mt-2 text-[16px] text-ink" data-id="element-3555" data-mp="3h2g5qyafivdg">{featured.focus}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-md border border-line bg-mist p-7" data-id="element-3556" data-mp="3lupy0kz0o7iw">
              <SessionTime webinar={featured} data-id="element-3557" data-mp="3qrpv46dw1zxv" />
              <div className="mt-6 border-t border-line pt-6" data-id="element-3558" data-mp="3vs44psv51hy0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-3559" data-mp="3vtp1lp4xdcz1">Speakers</p>
                <ul className="mt-3 space-y-2" data-id="element-3560" data-mp="4m2nt3ohru4g">
                  {featured.speakers.map(speaker => <li key={speaker.name} className="flex items-start gap-2.5 text-[15px] text-body" data-id="element-3561" data-mp="1p1se2wro2ekj">
                      <UsersIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-3562" data-mp="1k6v9q35c0tdz" />
                      <span data-id="element-3563" data-mp="1f7s4ahlfwl5h">
                        <span className="text-ink" data-id="element-3564" data-mp="1a6ulq3wipp3g">{speaker.name}</span>, {speaker.role}
                      </span>
                    </li>)}
                </ul>
              </div>
              <Button to={`/webinars/${featured.slug}`} size="lg" className="mt-7 w-full justify-center" data-id="element-3565" data-mp="22vjrwhcfkkm2">
                Register for this session
              </Button>
            </div>
          </div>
        </Section>}

      {/* Calendar */}
      <Section id="calendar" tone="mist" aria-labelledby="calendar-heading" data-id="element-3566" data-mp="1t4xdurkd425h">
        <SectionHeader id="calendar-heading" kicker="The full calendar" title="Twelve sessions, one a month." lead="Each session stands alone, so you can take the ones that matter to your unit. Registrants receive the recording and a one page summary whether or not they attend." data-id="element-3567" data-mp="2qlw3q3asr1hx" />

        <ol className="mt-12 divide-y divide-line border-y border-line" data-id="element-3568" data-mp="oxrn3r8lh445">
          {WEBINARS.map(webinar => {
          const past = webinarStatus(webinar) === 'past';
          return <li key={webinar.slug} data-id="element-3569" data-mp="f40ymmcgwtog">
                <Link to={`/webinars/${webinar.slug}`} className="group grid gap-4 py-7 transition-colors duration-200 hover:bg-panel/60 lg:grid-cols-[150px_1fr_auto] lg:items-center lg:gap-8" data-id="element-3570" data-mp="16cjlv530grb">
                  <div data-id="element-3571" data-mp="3nevlcc74at4f">
                    <p className="font-display text-[19px] leading-none text-gold" data-id="element-3572" data-mp="3nfnfrjozakt4">{webinar.month}</p>
                    <p className="mt-2 text-small text-muted" data-id="element-3573" data-mp="3se8fzj48d4hk">{sessionDateLabel(webinar)}</p>
                  </div>

                  <div data-id="element-3574" data-mp="1qksd9nuvbz68">
                    <h3 className="font-display text-[20px] leading-snug text-ink sm:text-[23px]" data-id="element-3575" data-mp="1qjzgnfpmiq1t">
                      {webinar.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-body" data-id="element-3576" data-mp="364048wlhfdhd">
                      {webinar.focus} · {webinar.audience}
                    </p>
                  </div>

                  <span className="inline-flex items-center gap-2 text-[15px] font-medium text-royal" data-id="element-3577" data-mp="3ku7dclplnk5e">
                    {past ? 'Watch the replay' : 'Register'}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3578" data-mp="3tsjbd8sspk7v" />
                  </span>
                </Link>
              </li>;
        })}
        </ol>
      </Section>

      {/* Replays */}
      <Section aria-labelledby="replays" data-id="element-3579" data-mp="2l6smpc9kypu">
        <Reveal data-id="element-3580" data-mp="29d7mluiwhddh">
          <Link to="/webinars/replays" className="group flex flex-col gap-6 rounded-md border border-line bg-panel p-8 transition-colors duration-200 hover:border-gold sm:flex-row sm:items-center sm:justify-between" data-id="element-3581" data-mp="29er2hk0n9meq">
            <div className="max-w-2xl" data-id="element-3582" data-mp="1ijmjecxavloo">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-3583" data-mp="1njs9honx904n">
                Replay library
              </p>
              <h2 id="replays" className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-3584" data-mp="1sdd73fllrs3b">
                Missed a session
              </h2>
              <p className="mt-3 text-[16px] text-body" data-id="element-3585" data-mp="znedr80dl8su">
                {replays.length > 0 ? `${replays.length} recorded ${replays.length === 1 ? 'session' : 'sessions'}, each with its one page summary.` : 'Recordings appear here after each session, each with its one page summary.'}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-royal" data-id="element-3586" data-mp="1dk1apasbvyyr">
              <PlayCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-3587" data-mp="2l2ag0s3xccje" />
              Open the library
            </span>
          </Link>
        </Reveal>
      </Section>
    </>;
}